import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { AlertCircle, CheckCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';

function RequisitionApproval({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [requisition, setRequisition] = useState(null);
  const [selectedParts, setSelectedParts] = useState({});
  const [overrideGrade, setOverrideGrade] = useState('');
  const [approverNotes, setApproverNotes] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchingForPart, setSearchingForPart] = useState(null);
  
  useEffect(() => {
    fetchRequisition();
  }, [id]);
  
  const fetchRequisition = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/requisitions/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const req = response.data;
      setRequisition(req);
      setOverrideGrade(req.grade_prediction?.if_all_parts_replaced || 'B');
      
      // Initialize selected parts (default to original)
      const selections = {};
      req.spare_parts?.forEach(part => {
        selections[part.part_id] = {
          selected: true,
          use_original: true,
          alternative: null,
          notes: ''
        };
      });
      setSelectedParts(selections);
    } catch (error) {
      console.error('Error fetching requisition:', error);
      alert('Failed to load requisition');
      navigate('/requisitions');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePartSelection = (partId, selected) => {
    setSelectedParts(prev => ({
      ...prev,
      [partId]: { ...prev[partId], selected }
    }));
  };
  
  const handlePartChange = (partId, useOriginal, alternative = null) => {
    setSelectedParts(prev => ({
      ...prev,
      [partId]: { ...prev[partId], use_original: useOriginal, alternative }
    }));
  };
  
  const handleApprove = async () => {
    try {
      // Build approval data
      const partDecisions = requisition.spare_parts.map(part => {
        const selection = selectedParts[part.part_id];
        if (!selection?.selected) {
          return {
            part_id: part.part_id,
            approval_status: 'Rejected',
            approved_quantity: 0,
            approver_notes: 'Not required'
          };
        }
        
        return {
          part_id: part.part_id,
          approval_status: selection.use_original ? 'Approved' : 'Modified',
          approved_quantity: 1,
          changed_to_part: selection.alternative,
          approver_notes: selection.notes || 'Approved'
        };
      });
      
      await axios.post(
        `${API}/api/requisitions/${id}/approve`,
        {
          requisition_id: id,
          overall_decision: 'approve',
          part_decisions: partDecisions,
          grade_decision: overrideGrade,
          approver_notes: approverNotes
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      alert('Requisition approved successfully!');
      navigate('/requisitions');
    } catch (error) {
      console.error('Error approving:', error);
      alert('Failed to approve: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleReject = async () => {
    if (!approverNotes.trim()) {
      alert('Please provide rejection reason in notes');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/requisitions/${id}/approve`,
        {
          requisition_id: id,
          overall_decision: 'reject',
          part_decisions: [],
          grade_decision: '',
          approver_notes: approverNotes
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      alert('Requisition rejected');
      navigate('/requisitions');
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Failed to reject');
    }
  };
  
  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-[#2b3380] mb-4"></div>
            <p className="text-gray-600">Loading requisition...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!requisition) return null;
  
  const device = requisition.device || {};
  const gradePred = requisition.grade_prediction || {};
  const iqcParams = requisition.iqc_parameters || [];
  
  // Calculate recommended grade based on selected parts
  const selectedPartsCount = Object.values(selectedParts).filter(s => s.selected).length;
  const totalPartsCount = requisition.spare_parts?.length || 0;
  const recommendedGrade = gradePred.if_all_parts_replaced || 'B';
  
  // Calculate total cost
  const totalCost = requisition.spare_parts?.reduce((sum, part) => {
    if (selectedParts[part.part_id]?.selected) {
      const selectedPart = selectedParts[part.part_id];
      if (!selectedPart.use_original && selectedPart.alternative) {
        return sum + (selectedPart.alternative.estimated_cost || 0);
      }
      return sum + (part.estimated_cost || 0);
    }
    return sum;
  }, 0) || 0;
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50 pb-8">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>
                  TRC Approval: {requisition.requisition_number}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {device.brand} {device.model} | DID: {device.did || device.supplier_ref}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center gap-2"
                >
                  <X className="w-5 h-5" />
                  Reject Device
                </button>
                <button
                  onClick={handleApprove}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve & Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
          {/* Device Overview with Images */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Images */}
              <div className="col-span-2">
                <div className="bg-gray-100 rounded-lg p-4 h-96 flex items-center justify-center">
                  {requisition.images && requisition.images.length > 0 ? (
                    <div className="relative w-full h-full">
                      <img
                        src={`data:image/jpeg;base64,${requisition.images[currentImageIndex]}`}
                        alt="Device"
                        className="w-full h-full object-contain"
                      />
                      {requisition.images.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                          <button
                            onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <span className="px-4 py-2 bg-white rounded-full shadow-lg text-sm font-medium">
                            {currentImageIndex + 1} / {requisition.images.length}
                          </span>
                          <button
                            onClick={() => setCurrentImageIndex(Math.min(requisition.images.length - 1, currentImageIndex + 1))}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <div className="text-6xl mb-2">📸</div>
                      <p>No images uploaded</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Device Info */}
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Device Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-1 border-b border-blue-200">
                      <span className="text-gray-600 font-medium">Job ID:</span>
                      <span className="font-semibold">{device.job_id}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-blue-200">
                      <span className="text-gray-600 font-medium">IQC Status:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        device.iqc_status === 'TF' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {device.iqc_status === 'TF' ? 'Trouble Found' : 'No Issues'}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-blue-200">
                      <span className="text-gray-600 font-medium">Current Grade:</span>
                      <span className="font-bold text-xl" style={{ color: ZOBOX_PRIMARY }}>{device.current_grade}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600 font-medium">Engineer:</span>
                      <span className="font-semibold">{requisition.engineer?.engineer_name || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
                  <h4 className="text-sm font-bold text-red-900 mb-2">Reported Issue:</h4>
                  <p className="text-sm text-red-800">{device.issue}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* IQC Failures & Parts Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: ZOBOX_PRIMARY }}>
              🔧 Parts Selection ({selectedPartsCount} of {totalPartsCount} selected)
            </h2>
            
            <div className="space-y-4">
              {requisition.spare_parts?.map((part) => {
                // Find related IQC failures
                const relatedIQC = iqcParams.filter(p => p.related_part_code === part.part_code);
                const selection = selectedParts[part.part_id] || {};
                
                return (
                  <div key={part.part_id} className={`border-2 rounded-lg p-4 transition ${
                    selection.selected ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-gray-50'
                  }`}>
                    <div className="flex items-start gap-4">
                      {/* Selection Checkbox */}
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={selection.selected || false}
                          onChange={(e) => handlePartSelection(part.part_id, e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </div>
                      
                      <div className="flex-1">
                        {/* IQC Failures */}
                        {relatedIQC.length > 0 && (
                          <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded">
                            <p className="text-xs font-bold text-red-900 mb-1">❌ IQC Failures:</p>
                            {relatedIQC.map((iqc, i) => (
                              <p key={i} className="text-xs text-red-800">
                                • {iqc.parameter_name}: <span className="font-semibold">{iqc.value}</span> {iqc.expected && `(Expected: ${iqc.expected})`}
                              </p>
                            ))}
                          </div>
                        )}
                        
                        {/* Part Details */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{part.part_name}</h3>
                            <p className="text-sm text-gray-600">Code: <span className="font-mono">{part.part_code}</span></p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold" style={{ color: ZOBOX_PRIMARY }}>₹{part.estimated_cost?.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{part.warehouse_location || 'N/A'}</p>
                          </div>
                        </div>
                        
                        {/* Stock Status */}
                        <div className="mb-3">
                          {part.stock_status === 'In_Stock' ? (
                            <div className="flex items-center gap-2 p-2 bg-green-100 border border-green-300 rounded text-sm">
                              <CheckCircle className="w-4 h-4 text-green-700" />
                              <span className="font-medium text-green-800">IN STOCK ({part.available_qty} available)</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-sm">
                              <AlertCircle className="w-4 h-4 text-yellow-700" />
                              <span className="font-medium text-yellow-800">NEEDS PURCHASE (Will create PO)</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Part Selection Options */}
                        {selection.selected && (
                          <div className="space-y-2">
                            {/* Original Part */}
                            <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                              selection.use_original ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-300 hover:border-gray-400'
                            }`}>
                              <input
                                type="radio"
                                name={`part-${part.part_id}`}
                                checked={selection.use_original || false}
                                onChange={() => handlePartChange(part.part_id, true)}
                                className="w-4 h-4 text-blue-600"
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">✅ Use Original Part</p>
                                <p className="text-xs text-gray-600">{part.part_name} - ₹{part.estimated_cost?.toLocaleString()}</p>
                              </div>
                            </label>
                            
                            {/* Compatible Alternatives */}
                            {part.compatible_parts?.map((alt, i) => (
                              <label key={i} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                                !selection.use_original && selection.alternative?.part_code === alt.part_code
                                  ? 'bg-purple-50 border-purple-500'
                                  : 'bg-white border-gray-300 hover:border-gray-400'
                              }`}>
                                <input
                                  type="radio"
                                  name={`part-${part.part_id}`}
                                  checked={!selection.use_original && selection.alternative?.part_code === alt.part_code}
                                  onChange={() => handlePartChange(part.part_id, false, alt)}
                                  className="w-4 h-4 text-purple-600"
                                />
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">🔄 {alt.part_name}</p>
                                  <p className="text-xs text-gray-600">
                                    Code: {alt.part_code} | Stock: {alt.available_qty || 0} | Cost: ₹{alt.estimated_cost?.toLocaleString() || 0}
                                  </p>
                                </div>
                              </label>
                            ))}
                            
                            {/* Search Other */}
                            <button
                              onClick={() => {
                                setSearchingForPart(part);
                                setShowSearchModal(true);
                              }}
                              className="w-full p-3 bg-white border-2 border-dashed border-gray-400 rounded-lg text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-600 transition text-sm"
                            >
                              🔍 Not Listed - Search Other Part
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Grade Decision */}
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg shadow-md p-6 border-2 border-green-300">
            <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_PRIMARY }}>📊 Grade Decision</h2>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Recommended Grade */}
              <div className="bg-white rounded-lg p-4 border-2 border-blue-400">
                <h3 className="text-sm font-bold text-blue-900 mb-2">🤖 AI Recommended Grade</h3>
                <div className="text-center">
                  <div className="text-5xl font-bold text-blue-600 mb-2">{recommendedGrade}</div>
                  <p className="text-xs text-gray-600">Based on: Selected parts, IQC data, historical sales</p>
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-700">Confidence: <span className="font-bold">{gradePred.confidence || 85}%</span></p>
                    <p className="text-xs text-gray-700">Expected Sale: <span className="font-bold">₹{(gradePred.expected_selling_price || 45000).toLocaleString()}</span></p>
                    <p className="text-xs text-gray-700">Profit: <span className="font-bold text-green-600">₹{(gradePred.profit || 20000).toLocaleString()}</span></p>
                  </div>
                </div>
              </div>
              
              {/* Override Grade */}
              <div className="bg-white rounded-lg p-4 border-2 border-orange-400">
                <h3 className="text-sm font-bold text-orange-900 mb-2">👤 Your Final Decision</h3>
                <div className="text-center">
                  <select
                    value={overrideGrade}
                    onChange={(e) => setOverrideGrade(e.target.value)}
                    className="w-full text-5xl font-bold text-orange-600 border-2 border-orange-300 rounded-lg px-4 py-2 mb-2 text-center"
                  >
                    <option value="A+">A+</option>
                    <option value="A">A</option>
                    <option value="B+">B+</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </select>
                  {overrideGrade !== recommendedGrade && (
                    <p className="text-xs text-orange-700 font-semibold">⚠️ Override: AI will learn from this decision</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Summary */}
            <div className="mt-4 p-4 bg-white rounded-lg border border-gray-300">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Parts Selected</p>
                  <p className="text-2xl font-bold text-green-600">{selectedPartsCount} / {totalPartsCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Investment</p>
                  <p className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>₹{totalCost.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Final Grade</p>
                  <p className="text-2xl font-bold text-orange-600">{overrideGrade}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Notes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-3">📝 Approver Notes</h3>
            <textarea
              value={approverNotes}
              onChange={(e) => setApproverNotes(e.target.value)}
              rows={3}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any comments, special instructions, or rejection reasons..."
            />
          </div>
        </div>
      </div>
      
      {/* Search Part Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_PRIMARY }}>
              Search Alternative Part for {searchingForPart?.part_name}
            </h2>
            <p className="text-gray-600 mb-4">Full part catalog search coming soon...</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchingForPart(null);
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default RequisitionApproval;
